import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'aura-code-block',
  imports: [MatIconModule],
  template: `
    <div class="aura-code-block-wrapper">
      <button class="aura-code-block-copy-btn" (click)="copyCode()" [attr.aria-label]="'Copier le code'">
        <mat-icon>{{ copied() ? 'check' : 'content_copy' }}</mat-icon>
      </button>
      <pre class="aura-code-block" [innerHTML]="highlightedCode()"></pre>
    </div>
  `,
  styles: `
    .aura-code-block-wrapper {
      position: relative;
      display: block;
      width: 100%;
    }
    .aura-code-block-copy-btn {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 0.375rem;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.375rem;
      transition: all 0.2s ease-in-out;
      outline: none;
      z-index: 10;
    }
    .aura-code-block-copy-btn:hover {
      background: rgba(255, 255, 255, 0.12);
      color: #ffffff;
      border-color: rgba(255, 255, 255, 0.25);
    }
    .aura-code-block-copy-btn mat-icon {
      font-size: 1rem;
      width: 1rem;
      height: 1rem;
    }
    .aura-code-block {
      background-color: #1e1e1e;
      color: #d4d4d4;
      padding: 1rem;
      padding-right: 2.75rem; /* Space for the copy button */
      border-radius: 0.5rem;
      font-family:
        ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
      font-size: 0.75rem;
      line-height: 1.5;
      overflow: auto;
      margin: 0;
    }
    .aura-code-block .key {
      color: #9cdcfe;
    }
    .aura-code-block .string {
      color: #ce9178;
    }
    .aura-code-block .number {
      color: #b5cea8;
    }
    .aura-code-block .boolean {
      color: #569cd6;
    }
    .aura-code-block .null {
      color: #569cd6;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodeBlockComponent {
  code = input.required<unknown>();
  copied = signal(false);

  highlightedCode = computed(() => {
    const rawVal = this.code();
    const json = typeof rawVal === 'string' ? rawVal : JSON.stringify(rawVal, null, 2);
    return this.syntaxHighlight(json);
  });

  copyCode(): void {
    const rawVal = this.code();
    const textToCopy = typeof rawVal === 'string' ? rawVal : JSON.stringify(rawVal, null, 2);
    void navigator.clipboard.writeText(textToCopy).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    });
  }

  private syntaxHighlight(json: string) {
    json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      (match) => {
        let cls = 'number';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'key';
          } else {
            cls = 'string';
          }
        } else if (/true|false/.test(match)) {
          cls = 'boolean';
        } else if (/null/.test(match)) {
          cls = 'null';
        }
        return '<span class="' + cls + '">' + match + '</span>';
      },
    );
  }
}
